// @flow

import * as React from 'react';

import { NavigableListItem } from '@ta/list.navigable-list';

import type { FetcherResult } from '../Typeahead';
import BasicResult from './BasicResult';
import type { TypeaheadResult } from '../TypeaheadResult';

type Props = {|
  /** The TypeaheadJsonFetcher result */
  result: FetcherResult,

  /** An optional callback if a result is selected */
  onSelection?: (
    event: SyntheticEvent<HTMLElement> | KeyboardEvent,
    result: TypeaheadResult,
  ) => void,

  /** An optional callback for if/when the mouse moves over the result */
  onMouseMove?: (event: SyntheticMouseEvent<HTMLElement>) => mixed,

  /** True if this result is active. Defaults to false */
  active?: boolean,
|};

/**
 * Create a basic typeahead search result from /TypeaheadJson?action=GEONAVI.
 *
 * GeoNavi is a feature used in some APAC markets to allow users to navigate the geo hierarhcy with their mouse instead
 * of typing out names.
 *
 * The result may either represent a location to navigate to (i.e. type: 'location_id') or a location to navigate the
 * geonavi hierarchy to (i.e. type: 'geonavi').
 *
 * Selecting a result of this type will trigger a call to onSelection() with a TypeaheadResult of either:
 * { type: "geonavi", value: <geo_id> }
 * or
 * { type: "location_id", value: <geo_id> }
 *
 * Note that this is intended to be used inside a <NavigableList/>
 */
class GeoNaviResult extends React.Component<Props> {
  static defaultProps = {
    onSelection: undefined,
    onMouseMove: undefined,
    active: false,
  };

  onSelection = (event: SyntheticEvent<HTMLElement> | KeyboardEvent) => {
    if (this.props.onSelection && typeof this.props.onSelection === 'function') {
      const {
        result: { geoId, back, forward },
      } = this.props;
      const isNavigational = back || forward;

      // by setting the type to 'geonavi' we indicate we don't want to navigate to a new page but to update
      // the typeahead context to a different geo.
      const type = isNavigational ? 'geonavi' : 'location_id';
      this.props.onSelection(event, { type, value: geoId });
    }
  };

  render() {
    let iconName;
    if (this.props.result.back) {
      iconName = 'single-chevron-left';
    } else if (this.props.result.forward) {
      iconName = 'single-chevron-right';
    }

    return (
      <NavigableListItem
        onSelection={this.onSelection}
        onMouseMove={this.props.onMouseMove}
        active={!!this.props.active}
      >
        <BasicResult
          title={this.props.result.name}
          chevronName={iconName}
          active={!!this.props.active}
        />
      </NavigableListItem>
    );
  }
}

export default GeoNaviResult;
